import { useState } from 'react';
import { NJInputSearch, NJAccordion, NJAccordionItem, NJText } from '@engie-group/fluid-design-system-react';
import { UM } from '../_components/tokens';
import { USAGE_STAGES } from './mocks';
import { sortEngieFirst } from './hooks';
import { LayerRow } from './LayerRow';
import type { LayerItem, UsageStage } from './types';

/**
 * Proposition B — Accordéon réorganisé par usage métier + Favoris + recherche.
 *
 * Évolution moins disruptive du panneau actuel : on garde l'accordéon vertical
 * connu des utilisateurs, mais les groupes techniques (Bâtiments/Réseaux/...)
 * sont remplacés par les 5 usages métier. Une section "Mes couches favorites"
 * reste épinglée en premier, et un champ de recherche limite la charge
 * cognitive quand un groupe contient beaucoup de couches.
 */
export function ProposalBAccordion({
  favoriteLayers,
  layers,
  filteredLayers,
  search,
  setSearch,
  toggleVisibility,
  toggleFavorite,
}: {
  favoriteLayers: LayerItem[];
  layers: LayerItem[];
  filteredLayers: LayerItem[];
  search: string;
  setSearch: (v: string) => void;
  toggleVisibility: (id: string) => void;
  toggleFavorite: (id: string) => void;
}) {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({ favorites: true, consumption: true });

  const toggleGroup = (id: string) => setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  const layersForStage = (stage: UsageStage) =>
    sortEngieFirst(filteredLayers.filter((l) => l.stages.includes(stage)));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', flexShrink: 0 }}>
        <NJInputSearch
          label="Rechercher une couche"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          resetButtonTitle="Effacer"
        />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 12px' }}>
        <NJAccordion isSeparated>
          <NJAccordionItem
            hasLeadingToggleIcon
            open={openGroups.favorites}
            onToggle={() => toggleGroup('favorites')}
          >
            <summary>★ Mes couches favorites ({favoriteLayers.length})</summary>
            <div style={{ padding: '4px 0' }}>
              {favoriteLayers.length === 0 && (
                <NJText scale="sm" style={{ color: UM.textDim }}>
                  Aucune couche favorite. Cliquez sur ★ à côté d'une couche pour l'ajouter ici.
                </NJText>
              )}
              {favoriteLayers.map((layer) => (
                <LayerRow
                  key={layer.id}
                  layer={layer}
                  onToggleVisibility={toggleVisibility}
                  onToggleFavorite={toggleFavorite}
                />
              ))}
            </div>
          </NJAccordionItem>

          {USAGE_STAGES.map((stage) => {
            const stageLayers = layersForStage(stage.id);
            if (search.trim() && stageLayers.length === 0) return null;
            return (
              <NJAccordionItem
                key={stage.id}
                hasLeadingToggleIcon
                open={openGroups[stage.id] ?? false}
                onToggle={() => toggleGroup(stage.id)}
              >
                <summary>{stage.label} ({layers.filter((l) => l.stages.includes(stage.id)).length})</summary>
                <div style={{ padding: '4px 0' }}>
                  <NJText scale="sm" style={{ color: UM.textMuted, marginBottom: 6, display: 'block' }}>
                    {stage.description}
                  </NJText>
                  {stageLayers.map((layer) => (
                    <LayerRow
                      key={layer.id}
                      layer={layer}
                      onToggleVisibility={toggleVisibility}
                      onToggleFavorite={toggleFavorite}
                    />
                  ))}
                </div>
              </NJAccordionItem>
            );
          })}
        </NJAccordion>
      </div>
    </div>
  );
}
